/** @jsxImportSource @emotion/react */
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  fetchProgramOptions,
  fetchBatchOptions,
  fetchPaymentMethodOptions,
  fetchCampusLocationOptions,
  fetchFollowUpStatusOptions,
  fetchApplicantStatusOptions,
} from 'helpers';
import Sidebar from 'components/layout/Sidebar/Sidebar';
import {
  FilterSection, FilterField, FilterActions, countActiveFilters,
} from 'components/layout/Sidebar/Filter';
import {
  CustomDatepicker as Datepicker,
  Select2,
  Select,
  CreatableSelect,
} from 'components/common';

import {
  filterConditionsContainerStyles,
  datepickerContainerStyles,
  select2ContainerStyles,
  selectContainerStyles,
} from './Sidebar.styles';

const PtpApplicantSidebar = ({
  search,
  startDate,
  endDate,
  setDateRange,
  filterCondition,
  setFilterCondition,
  handleChange,
  handleSelect2Change,
  handleSubmit,
  handleReset,
  // handleDownloadCSV,
  // isCsvDownloading,
}) => {
  const [options, setOptions] = useState({
    batchOptions: [],
    paymentMethodOptions: [],
    paymentStatusOptions: [],
    followUpStatusOptions: [],
    programOptions: [],
    campusLocationOptions: [],
    applicantStatusOptions: [],
  });

  const getPaymentOptions = () => fetchPaymentMethodOptions().then((data) => {
    const modifiedArr = data.filter((d) => d.value !== 'ISA');
    return modifiedArr;
  });
  const getBatchOptions = () => fetchBatchOptions(true).then((resp) => resp.map((d) => ({
    label: d.label,
    value: d.label,
  })));
  const getFollowUpStatusOptions = () => fetchFollowUpStatusOptions().then((resp) => resp);
  const getCampusLocationOptions = () => fetchCampusLocationOptions()
    .then((resp) => resp.map((d) => ({
      label: d.label,
      value: d.label,
    })));
  const getProgramOptions = () => fetchProgramOptions('PART_TIME').then((resp) => resp);
  const getApplicantStatusOptions = () => fetchApplicantStatusOptions().then((resp) => resp);

  useEffect(async () => {
    const [
      batchOptions,
      paymentMethodOptions,
      followUpStatusOptions,
      programOptions,
      campusLocationOptions,
      applicantStatusOptions,
    ] = await Promise.all([
      getBatchOptions(),
      getPaymentOptions(),
      getFollowUpStatusOptions(),
      getProgramOptions(),
      getCampusLocationOptions(),
      getApplicantStatusOptions(),
    ]);
    setOptions((prevState) => ({
      ...prevState,
      batchOptions,
      paymentMethodOptions,
      followUpStatusOptions,
      programOptions,
      campusLocationOptions,
      applicantStatusOptions,
    }));
  }, []);

  return (
    <Sidebar search={search} activeCount={countActiveFilters(filterCondition, startDate)}>
      <div css={filterConditionsContainerStyles}>
        <FilterSection title="Dates">
          <FilterField label="applied date">
            <Datepicker
              portalId="filter-panel-popover"
              containerStyles={datepickerContainerStyles}
              startDate={startDate}
              endDate={endDate}
              setDateRange={setDateRange}
              placeholder="date range"
            />
          </FilterField>
        </FilterSection>
        <FilterSection title="Program">
          <FilterField label="program">
            <Select2
              portalMenu
              containerStyles={select2ContainerStyles}
              defaultValue={{ label: 'program', value: '' }}
              options={options.programOptions}
              placeholder="program"
              onChange={(e) => handleSelect2Change(e, 'program')}
              value={filterCondition.program}
            />
          </FilterField>
          <FilterField label="batch enrolled">
            <Select2
              portalMenu
              containerStyles={select2ContainerStyles}
              defaultValue={{ label: 'batch enrolled', value: '' }}
              options={options.batchOptions}
              placeholder="batch enrolled"
              onChange={(e) => handleSelect2Change(e, 'lastBatch')}
              value={filterCondition.lastBatch}
            />
          </FilterField>
          <FilterField label="campus location">
            <Select
              containerStyles={selectContainerStyles}
              name="programLocation"
              options={options.campusLocationOptions}
              placeholder="campus location"
              value={filterCondition.programLocation}
              onChange={handleChange}
            />
          </FilterField>
        </FilterSection>
        <FilterSection title="Payment">
          <FilterField label="payment type">
            <CreatableSelect
              portalMenu
              name="paymentType"
              containerStyles={select2ContainerStyles}
              options={options.paymentMethodOptions}
              placeholder="payment type"
              filterCondition={filterCondition}
              setFilterCondition={setFilterCondition}
              value={filterCondition.paymentType}
            />
          </FilterField>
        </FilterSection>
        <FilterSection title="Status">
          <FilterField label="follow up status">
            <CreatableSelect
              portalMenu
              name="followUpStatus"
              containerStyles={select2ContainerStyles}
              options={options.followUpStatusOptions}
              placeholder="follow up status"
              filterCondition={filterCondition}
              setFilterCondition={setFilterCondition}
              value={filterCondition.followUpStatus}
            />
          </FilterField>
          <FilterField label="applicant status">
            <CreatableSelect
              portalMenu
              name="applicantStatuses"
              containerStyles={select2ContainerStyles}
              defaultValue={{ label: 'applicant status', value: '' }}
              options={options.applicantStatusOptions}
              placeholder="applicant status"
              filterCondition={filterCondition}
              setFilterCondition={setFilterCondition}
              value={filterCondition.applicantStatuses}
            />
          </FilterField>
        </FilterSection>
      </div>
      <FilterActions onApply={handleSubmit} onReset={handleReset} />
    </Sidebar>
  );
};

PtpApplicantSidebar.propTypes = {
  search: Sidebar.propTypes.search,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  setDateRange: PropTypes.func.isRequired,
  filterCondition: PropTypes.shape({
    program: PropTypes.string.isRequired,
    testResults: PropTypes.arrayOf(PropTypes.shape({})),
    lastBatch: PropTypes.string.isRequired,
    programLocation: PropTypes.string,
    paymentType: PropTypes.arrayOf(PropTypes.shape({})),
    followUpStatus: PropTypes.arrayOf(PropTypes.shape({})),
    applicantStatuses: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  setFilterCondition: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleSelect2Change: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleReset: PropTypes.func.isRequired,
  // handleDownloadCSV: PropTypes.func.isRequired,
  // isCsvDownloading: PropTypes.bool.isRequired,
};

PtpApplicantSidebar.defaultProps = {
  search: null,
  startDate: null,
  endDate: null,
};

export default PtpApplicantSidebar;
